import { AssessmentInvoiceSnippet, Practice, PracticeAccess, PracticeUserAccess } from '../../types'
import {
  formatDollarValue,
  formatPaymentType,
  getDateString,
  getMonthStringFromNumber,
  padZeros,
  separatePayments,
  toSearchString,
} from '../../utils'
import { invoiceStatuses, nyTimezoneOffset } from '../constants'
import {
  ChargedPaymentReceived,
  Invoice,
  InvoiceAssessmentPaymentsSnippet,
  PracticeInvoice,
  PracticeInvoiceOmission,
} from '../types'

export const getInvoiceMonthString = (date: Date) => {
  const month = date.getUTCMonth() + 1
  const year = date.getUTCFullYear()
  return `${month}-${year}`
}

// const getCurrentInvoiceMonthString = () => {
//   const currentTimeNY = new Date(Date.now() - nyTimezoneOffset)
//   return getInvoiceMonthString(currentTimeNY)
// }

export const getInvoiceId = (practiceId: string, monthString: string, numAttempts: number) =>
  `${practiceId}-${monthString}${numAttempts ? `-${numAttempts}` : ''}`

export const getInvoicePracticeId = (invoiceId: string) => {
  const [practiceId] = invoiceId.split('-')
  if (!practiceId) throw new Error(`Invalid invoiceId ${invoiceId}`)
  return practiceId
}

export const getCurrentPracticeInvoiceId = (practiceId: string, numAttempts: number) =>
  getInvoiceId(
    practiceId,
    getInvoiceMonthString(new Date(Date.now() - nyTimezoneOffset)),
    numAttempts,
  )

export const parseDateFromInvoiceId = (invoiceId: string) => {
  const [, monthStr, yearStr] = invoiceId.split('-')
  if (!monthStr || !yearStr) throw new Error(`Invalid invoiceId ${invoiceId}`)
  const month = parseInt(monthStr, 10) - 1
  if (Number.isNaN(month)) throw new Error(`Invalid month ${monthStr}`)
  const year = parseInt(yearStr, 10)
  if (Number.isNaN(year)) throw new Error(`Invalid year ${yearStr}`)
  return { month, year }
}

export const parseMonthStringFromInvoiceId = (invoiceId: string) => {
  const [, monthStr, yearStr] = invoiceId.split('-')
  if (!monthStr || !yearStr) throw new Error(`Invalid invoiceId ${invoiceId}`)
  const month = parseInt(monthStr, 10)
  const year = parseInt(yearStr, 10)
  if (Number.isNaN(month)) throw new Error(`Invalid month ${monthStr}`)
  if (Number.isNaN(year)) throw new Error(`Invalid year ${yearStr}`)
  return `${month}-${year}`
}
// 2 months ahead
export const getInvoiceDueDate = ({ year, month }: { year: number; month: number }) => {
  const date = month > 10 ? new Date(year + 1, month - 11, 25) : new Date(year, month + 1, 25)

  return date.toISOString()
}
// 1 month ahead
export const getInvoiceScheduleDate = ({ year, month }: { year: number; month: number }) => {
  const date = month > 10 ? new Date(year + 1, month - 11, 1) : new Date(year, month + 1, 1)

  return date.toISOString()
}

export const getAdjustedDepositsTotal = (assessment: InvoiceAssessmentPaymentsSnippet) => {
  const { deposits } = separatePayments(assessment.chargedPayments)
  const totalCharged = Object.values(deposits).reduce(
    (total, deposit) => total + deposit.invoicedAmount,
    0,
  )
  return Math.max(0, totalCharged)
}

export const getInvoiceAssessmentDepositsString = (
  assessment: InvoiceAssessmentPaymentsSnippet,
  assessmentId: string,
  omissions: Record<string, PracticeInvoiceOmission>,
): string | null => {
  const { deposits } = separatePayments(assessment.chargedPayments, assessmentId, omissions)
  const totalDepositsAmount = getAdjustedDepositsTotal(assessment)
  const datesString = Object.values(deposits)
    .reduce<string[]>((dates, deposit) => {
      const dateString = getDateString(deposit.date, 'short')
      if (!dates.includes(dateString)) {
        dates.push(dateString)
      }
      return dates
    }, [])
    .map(date => getDateString(date, 'short'))
    .join(', ')

  return totalDepositsAmount
    ? `${formatDollarValue(totalDepositsAmount)} in deposits paid by patient - ${datesString}`
    : null
}

export const getInvoiceAssessmentPaymentString = (payment: ChargedPaymentReceived) =>
  `${formatDollarValue(payment.invoicedAmount)} ${formatPaymentType(
    payment.type,
  )} paid by ${payment.paidBy} -  ${getDateString(payment.date, 'short')}`

export const getInvoiceSentFromStatus = (status: Invoice['status']) =>
  status === 'sent' || status === 'paid'

export const getInvoiceSent = (invoice: Invoice | AssessmentInvoiceSnippet) =>
  getInvoiceSentFromStatus(
    (invoice as Invoice).status || (invoice as AssessmentInvoiceSnippet).invoiceStatus,
  )

export const getInvoiceSendToUser = (
  practiceAccess: PracticeAccess | undefined,
): PracticeUserAccess | null => {
  const sendToId =
    practiceAccess?.sendInvoicesTo ??
    Object.entries(practiceAccess?.users ?? {})
      .sort(([, { addedOn: addedOnA }], [, { addedOn: addedOnB }]) => addedOnA - addedOnB)
      .filter(([, userAccess]) => userAccess.role === 'admin')?.[0]?.[0]
  if (!sendToId) return null
  return practiceAccess?.users?.[sendToId] ?? null
}

export const invoiceStatusSort = (
  a: AssessmentInvoiceSnippet | PracticeInvoice,
  b: AssessmentInvoiceSnippet | PracticeInvoice,
) => {
  const aAsSnippet = a as AssessmentInvoiceSnippet
  const aAsInvoice = a as PracticeInvoice
  const bAsSnippet = b as AssessmentInvoiceSnippet
  const bAsInvoice = b as PracticeInvoice
  const aStatus = aAsSnippet.invoiceStatus || aAsInvoice.status
  const bStatus = bAsSnippet.invoiceStatus || bAsInvoice.status

  return invoiceStatuses.indexOf(aStatus) - invoiceStatuses.indexOf(bStatus)
}

const getInvoiceAssessmentSnippetSearchString = (assessment: InvoiceAssessmentPaymentsSnippet) => {
  const { assessmentName, fname, lname, nickname } = assessment
  return [assessmentName, fname, lname, nickname]
    .map(m => m ?? '')
    .filter(s => !!s)
    .map(toSearchString)
    .join('|')
}
export const getInvoiceSearchString = (
  invoice: Omit<PracticeInvoice, 'searchString'>,
  practice: Practice | null,
) => {
  const { practiceName, assessments } = invoice
  const { fname, lname } = practice ?? {}
  const assessmentSearchString = Object.values(assessments)
    .map(getInvoiceAssessmentSnippetSearchString)
    .join('|')

  const invoiceStringified = [fname, lname, practiceName, assessmentSearchString]
    .filter(s => !!s)
    .map(s => toSearchString(s ?? ''))
    .join('|')
  return [assessmentSearchString, invoiceStringified].join('|')
}

// displayed invoice month is due/scheduled month, not payments month, so we need to add one month
export const getDisplayedInvoiceMonthYear = (month: number, year: number) => {
  const date = new Date(`${year}-${padZeros(month + 1, 2)}-01T00:00:00.000Z`)
  date.setUTCMonth(date.getUTCMonth() + 1)
  return `${getMonthStringFromNumber(date.getUTCMonth() + 1, true)} ${date.getUTCFullYear()}`
}

export const getDisplayedInvoiceMonth = (month: number, year: number) => {
  const date = new Date(`${year}-${padZeros(month + 1, 2)}-01T00:00:00.000Z`)
  date.setUTCMonth(date.getUTCMonth() + 1)
  return getMonthStringFromNumber(date.getUTCMonth() + 1)
}

export const getInvoiceStatusName = (status: PracticeInvoice['status']) => {
  switch (status) {
    case 'draft':
      return 'Draft'
    case 'paid':
      return 'Paid'
    case 'pending-approval':
      return 'Pending Approval'
    case 'scheduled':
      return 'Scheduled'
    case 'sent':
      return 'Sent'
    default:
      return 'Unknown'
  }
}

export const getAssessmentInvoiceFee = (snippet: AssessmentInvoiceSnippet) => {
  const { chargedPayments } = snippet

  return Object.values(chargedPayments).reduce((total, payment) => total + payment.feeAmount, 0)
}

export const sortInvoiceSnippetsBySent = (invoices: Record<string, AssessmentInvoiceSnippet>) =>
  Object.entries(invoices).sort(([, a], [, b]) => {
    // unsent should show first
    const aSent = getInvoiceSent(a)
    const bSent = getInvoiceSent(b)
    if (aSent && bSent) return 0
    if (!aSent) return -1
    if (!bSent) return 1
    // if both same status, sort by scheduled date
    if (!a.scheduledFor || !b.scheduledFor) return 0
    return a.scheduledFor.localeCompare(b.scheduledFor)
  })
